import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
// ImpersonationBanner is mounted globally in App.js — do not duplicate here.
import {
  Building2, Users, GraduationCap, UserCog, Download, Key,
  Trash2, Plus, Search, Loader2, ArrowLeft, Settings, School,
  BarChart3, FileSpreadsheet, RefreshCw, UserPlus, BookOpen, UserX,
  Link2, Unlink, CheckCircle, XCircle, AlertCircle
} from 'lucide-react';

const OrgAdminDashboard = () => {
  const { api, user, impersonateUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  
  // Reset password dialog
  const [resetDialog, setResetDialog] = useState({ open: false, userId: null, userName: '' });
  const [newPassword, setNewPassword] = useState('');
  const [resetting, setResetting] = useState(false);
  
  // Delete user dialog
  const [deleteDialog, setDeleteDialog] = useState({ open: false, userId: null, userName: '' });
  const [deleting, setDeleting] = useState(false);
  
  // Add Staff dialog
  const [addStaffDialog, setAddStaffDialog] = useState(false);
  const [newStaff, setNewStaff] = useState({ name: '', email: '', password: '', role: 'teacher' });
  const [addingStaff, setAddingStaff] = useState(false);
  
  // Add Class dialog
  const [addClassDialog, setAddClassDialog] = useState(false);
  const [newClass, setNewClass] = useState({ name: '', description: '', grade_level: '', max_students: 30 });
  const [addingClass, setAddingClass] = useState(false);
  
  // Classes data
  const [classes, setClasses] = useState([]);
  
  // Google Classroom
  const [gcStatus, setGcStatus] = useState(null);
  const [gcCourses, setGcCourses] = useState([]);
  const [gcConnecting, setGcConnecting] = useState(false);
  const [gcSyncing, setGcSyncing] = useState({});
  const [gcLoading, setGcLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [dashRes, usersRes, classesRes] = await Promise.all([
        api.get('/org-admin/dashboard'),
        api.get('/org-admin/users'),
        api.get('/org-admin/classes')
      ]);
      setDashboardData(dashRes.data);
      setUsers(usersRes.data);
      setClasses(classesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      if (error.response?.status === 403) {
        toast.error('Organization admin access required');
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setResetting(true);
    try {
      await api.post(`/org-admin/reset-password/${resetDialog.userId}?new_password=${encodeURIComponent(newPassword)}`);
      toast.success('Password reset successfully');
      setResetDialog({ open: false, userId: null, userName: '' });
      setNewPassword('');
    } catch (error) {
      toast.error('Failed to reset password');
    } finally {
      setResetting(false);
    }
  };

  const handleDeleteUser = async () => {
    setDeleting(true);
    try {
      await api.delete(`/org-admin/users/${deleteDialog.userId}`);
      toast.success('User deleted successfully');
      setDeleteDialog({ open: false, userId: null, userName: '' });
      // Refresh users list
      const usersRes = await api.get('/org-admin/users');
      setUsers(usersRes.data);
    } catch (error) {
      toast.error('Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  // Impersonate a user within the organization
  const handleImpersonateUser = async (userId, userName) => {
    try {
      await impersonateUser(userId, true); // true = org admin
      toast.success(`Now viewing as ${userName}`);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to impersonate user');
    }
  };

  const handleExportUsers = async () => {
    try {
      const response = await api.get('/org-admin/export/users');
      // Download CSV
      const blob = new Blob([response.data.csv_data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `users_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${response.data.count} users`);
    } catch (error) {
      toast.error('Failed to export users');
    }
  };

  const handleExportStudents = async () => {
    try {
      const response = await api.get('/org-admin/export/students');
      const blob = new Blob([response.data.csv_data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `students_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${response.data.count} students`);
    } catch (error) {
      toast.error('Failed to export students');
    }
  };

  const handleAddStaff = async () => {
    if (!newStaff.name || !newStaff.email || !newStaff.password) {
      toast.error('Please fill all required fields');
      return;
    }
    if (newStaff.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setAddingStaff(true);
    try {
      await api.post('/org-admin/add-staff', newStaff);
      toast.success('Staff member added successfully');
      setAddStaffDialog(false);
      setNewStaff({ name: '', email: '', password: '', role: 'teacher' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add staff');
    } finally {
      setAddingStaff(false);
    }
  };

  const handleAddClass = async () => {
    if (!newClass.name) {
      toast.error('Please enter a class name');
      return;
    }
    
    setAddingClass(true);
    try {
      await api.post('/org-admin/classes', newClass);
      toast.success('Class created successfully');
      setAddClassDialog(false);
      setNewClass({ name: '', description: '', grade_level: '', max_students: 30 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create class');
    } finally {
      setAddingClass(false);
    }
  };

  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      await api.put(`/org-admin/staff/${userId}/role?new_role=${newRole}`);
      toast.success('Role updated successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  // Google Classroom functions
  const fetchGoogleClassroomStatus = async () => {
    setGcLoading(true);
    try {
      const res = await api.get('/google-classroom/status');
      setGcStatus(res.data);
      if (res.data.org_connected || res.data.user_connected) {
        fetchGoogleClassroomCourses();
      }
    } catch (error) {
      console.error('Failed to fetch Google Classroom status:', error);
    } finally {
      setGcLoading(false);
    }
  };

  const fetchGoogleClassroomCourses = async () => {
    try {
      const res = await api.get('/google-classroom/courses');
      setGcCourses(res.data.courses || []);
    } catch (error) {
      console.error('Failed to fetch courses:', error);
    }
  };

  const handleGoogleClassroomConnect = async () => {
    setGcConnecting(true);
    try {
      const res = await api.get('/google-classroom/authorize', { params: { return_path: '/organization/admin' } });
      if (res.data?.authorization_url) {
        window.location.assign(res.data.authorization_url);
      }
      toast.success(res.data.message || 'Google Classroom connected!');
      await fetchGoogleClassroomStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect Google Classroom');
    } finally {
      setGcConnecting(false);
    }
  };

  const handleGoogleClassroomDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect Google Classroom? This will remove the integration for all teachers in your organization.')) return;
    
    try {
      await api.post('/google-classroom/disconnect');
      toast.success('Google Classroom disconnected');
      setGcStatus({ connected: false, org_connected: false, user_connected: false });
      setGcCourses([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disconnect');
    }
  };

  const handleGoogleClassroomSync = async (courseId) => {
    setGcSyncing(prev => ({ ...prev, [courseId]: true }));
    try {
      const res = await api.post(`/google-classroom/courses/${courseId}/sync-roster`);
      toast.success(res.data.message || 'Course synced to Zazu!');
      fetchData(); // Refresh classes
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync course');
    } finally {
      setGcSyncing(prev => ({ ...prev, [courseId]: false }));
    }
  };

  // Load Google Classroom data when tab changes
  useEffect(() => {
    if (activeTab === 'google-classroom' && !gcStatus) {
      fetchGoogleClassroomStatus();
    }
  }, [activeTab]);

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Impersonation Banner */}
      {/* ImpersonationBanner is mounted globally in App.js */}
      
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/organization">
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                  <UserCog className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="font-bold text-xl text-slate-800">Admin Dashboard</h1>
                  <p className="text-sm text-slate-500">{dashboardData?.organization?.name}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={fetchData} size="sm">
                <RefreshCw className="w-4 h-4 mr-1" /> Refresh
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid sm:grid-cols-4 gap-4 mb-8">
          <Card className="cursor-pointer hover:shadow-md transition-all" onClick={() => setActiveTab('users')}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Teachers</p>
                  <p className="text-2xl font-bold text-slate-900">{dashboardData?.stats?.teachers || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="cursor-pointer hover:shadow-md transition-all" onClick={() => setActiveTab('students')}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <GraduationCap className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Students</p>
                  <p className="text-2xl font-bold text-slate-900">{dashboardData?.stats?.students || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="cursor-pointer hover:shadow-md transition-all"
            onClick={() => setActiveTab('classes')}
            data-testid="classes-stat-widget"
          >
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <School className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Classes</p>
                  <p className="text-2xl font-bold text-slate-900">{dashboardData?.stats?.classes || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="cursor-pointer hover:shadow-md transition-all"
            onClick={() => setActiveTab('overview')}
            data-testid="licenses-stat-widget"
          >
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Licenses</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {dashboardData?.stats?.licenses_used || 0}/{dashboardData?.stats?.licenses_total || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="users">Staff Management</TabsTrigger>
            <TabsTrigger value="classes">Classes</TabsTrigger>
            <TabsTrigger value="google-classroom" data-testid="gc-tab">Google Classroom</TabsTrigger>
            <TabsTrigger value="data">Data & Export</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Organization Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-slate-500">Name</span>
                    <span className="font-medium">{dashboardData?.organization?.name}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-slate-500">Type</span>
                    <span className="font-medium capitalize">{dashboardData?.organization?.organization_type}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-slate-500">Contact Email</span>
                    <span className="font-medium">{dashboardData?.organization?.contact_email}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-slate-500">Subscription</span>
                    <span className="font-medium capitalize">{dashboardData?.organization?.subscription_plan}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-slate-500">Onboarding</span>
                    <span className={`font-medium ${dashboardData?.organization?.onboarding_completed ? 'text-green-600' : 'text-amber-600'}`}>
                      {dashboardData?.organization?.onboarding_completed ? 'Complete' : 'Pending'}
                    </span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button className="w-full justify-start" onClick={() => setAddStaffDialog(true)} data-testid="add-staff-btn">
                    <UserPlus className="w-4 h-4 mr-2" /> Add Staff Member
                  </Button>
                  <Button className="w-full justify-start" variant="outline" onClick={() => setAddClassDialog(true)} data-testid="add-class-btn">
                    <BookOpen className="w-4 h-4 mr-2" /> Create New Class
                  </Button>
                  <Link to="/organization" className="block">
                    <Button className="w-full justify-start" variant="outline">
                      <Building2 className="w-4 h-4 mr-2" /> Go to Organization Dashboard
                    </Button>
                  </Link>
                  <Button className="w-full justify-start" variant="outline" onClick={handleExportUsers}>
                    <Download className="w-4 h-4 mr-2" /> Export Users CSV
                  </Button>
                  <Button className="w-full justify-start" variant="outline" onClick={handleExportStudents}>
                    <FileSpreadsheet className="w-4 h-4 mr-2" /> Export Students CSV
                  </Button>
                  <Button
                    className="w-full justify-start"
                    variant="outline"
                    onClick={async () => {
                      try {
                        const res = await api.post('/billing/portal', { origin: window.location.origin });
                        if (res.data?.url) window.location.href = res.data.url;
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Failed to open billing portal');
                      }
                    }}
                    data-testid="manage-billing-btn"
                  >
                    <Key className="w-4 h-4 mr-2" /> Manage Billing & Payment Method
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* User Management Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Staff Management</CardTitle>
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                      <Input
                        placeholder="Search staff..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9 w-64"
                        data-testid="user-search-input"
                      />
                    </div>
                    <Button size="sm" onClick={() => setAddStaffDialog(true)}>
                      <Plus className="w-4 h-4 mr-1" /> Add Staff
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Classes</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.map(u => (
                      <TableRow key={u.id}>
                        <TableCell className="font-medium">{u.name}</TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>
                          <Select
                            value={u.account_type}
                            onValueChange={(v) => handleUpdateUserRole(u.id, v)}
                          >
                            <SelectTrigger className="w-28 h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="teacher">Teacher</SelectItem>
                              <SelectItem value="org_admin">Admin</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>{u.class_count || 0}</TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              title="View as this user"
                              onClick={() => handleImpersonateUser(u.id, u.name || u.email)}
                              disabled={u.account_type === 'org_admin'}
                              data-testid={`impersonate-${u.id}`}
                            >
                              <UserX className="w-3 h-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setResetDialog({ open: true, userId: u.id, userName: u.name })}
                              data-testid={`reset-pwd-${u.id}`}
                            >
                              <Key className="w-3 h-3 mr-1" /> Reset
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => setDeleteDialog({ open: true, userId: u.id, userName: u.name })}
                              data-testid={`delete-user-${u.id}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {filteredUsers.length === 0 && (
                  <p className="text-center py-8 text-slate-500">No users found</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Classes Tab */}
          <TabsContent value="classes">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Classes</CardTitle>
                  <Button size="sm" onClick={() => setAddClassDialog(true)}>
                    <Plus className="w-4 h-4 mr-1" /> Create Class
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {classes.length === 0 ? (
                  <div className="text-center py-12">
                    <School className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500 mb-4">No classes created yet</p>
                    <Button onClick={() => setAddClassDialog(true)}>
                      <Plus className="w-4 h-4 mr-2" /> Create Your First Class
                    </Button>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {classes.map((cls) => (
                      <Card key={cls.id} className="hover:shadow-md transition-all cursor-pointer">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                              <BookOpen className="w-5 h-5 text-indigo-600" />
                            </div>
                            <span className="text-xs bg-slate-100 px-2 py-1 rounded-full text-slate-600">
                              {cls.grade_level || 'All Grades'}
                            </span>
                          </div>
                          <h3 className="font-semibold text-slate-800 mb-1">{cls.name}</h3>
                          {cls.description && (
                            <p className="text-sm text-slate-500 mb-2 line-clamp-2">{cls.description}</p>
                          )}
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-500">
                              <GraduationCap className="w-4 h-4 inline mr-1" />
                              {cls.student_count || 0} students
                            </span>
                            {cls.teacher && (
                              <span className="text-slate-500 truncate max-w-[120px]">
                                {cls.teacher.name}
                              </span>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Data & Export Tab */}
          <TabsContent value="data">
            <div className="grid lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Download className="w-5 h-5 text-indigo-500" />
                    Export Data
                  </CardTitle>
                  <CardDescription>
                    Download your organization's data in CSV format
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Button 
                    className="w-full justify-start" 
                    variant="outline"
                    onClick={handleExportUsers}
                  >
                    <Users className="w-4 h-4 mr-2" /> Export All Users
                  </Button>
                  <Button 
                    className="w-full justify-start" 
                    variant="outline"
                    onClick={handleExportStudents}
                  >
                    <GraduationCap className="w-4 h-4 mr-2" /> Export All Students
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-green-500" />
                    Data Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="text-slate-600">Total Users</span>
                      <span className="font-bold">{users.length}</span>
                    </div>
                    <div className="flex justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="text-slate-600">Teachers</span>
                      <span className="font-bold">{dashboardData?.stats?.teachers || 0}</span>
                    </div>
                    <div className="flex justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="text-slate-600">Students</span>
                      <span className="font-bold">{dashboardData?.stats?.students || 0}</span>
                    </div>
                    <div className="flex justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="text-slate-600">Classes</span>
                      <span className="font-bold">{dashboardData?.stats?.classes || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Google Classroom Tab */}
          <TabsContent value="google-classroom">
            <div className="space-y-6">
              {/* Connection Status Card */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        {gcStatus?.org_connected || gcStatus?.user_connected ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <XCircle className="w-5 h-5 text-slate-400" />
                        )}
                        Google Classroom Integration
                      </CardTitle>
                      <CardDescription>
                        {gcStatus?.org_connected 
                          ? `Organization connected as ${gcStatus.org_email}`
                          : gcStatus?.user_connected
                            ? `Your account connected as ${gcStatus.user_email}`
                            : 'Connect Google Classroom to import classes and sync students'
                        }
                      </CardDescription>
                    </div>
                    {gcStatus?.org_connected || gcStatus?.user_connected ? (
                      <Button 
                        variant="outline" 
                        onClick={handleGoogleClassroomDisconnect} 
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid="gc-disconnect-btn"
                      >
                        <Unlink className="w-4 h-4 mr-2" />
                        Disconnect
                      </Button>
                    ) : (
                      <Button 
                        onClick={handleGoogleClassroomConnect} 
                        disabled={gcConnecting}
                        className="bg-blue-600 hover:bg-blue-700"
                        data-testid="gc-connect-btn"
                      >
                        {gcConnecting ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Link2 className="w-4 h-4 mr-2" />
                        )}
                        Connect Google Classroom
                      </Button>
                    )}
                  </div>
                </CardHeader>
                {(gcStatus?.org_connected || gcStatus?.user_connected) && gcStatus?.last_sync && (
                  <CardContent>
                    <p className="text-sm text-slate-500">
                      Last synced: {new Date(gcStatus.last_sync).toLocaleString()}
                    </p>
                  </CardContent>
                )}
              </Card>

              {/* Info Banner */}
              <Card className="border-blue-200 bg-blue-50">
                <CardContent className="p-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-blue-800">Organization-Wide Integration</p>
                    <p className="text-sm text-blue-700">
                      When you connect Google Classroom, all teachers in your organization can import 
                      their classes and sync student rosters directly from Google Classroom.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Courses List */}
              {(gcStatus?.org_connected || gcStatus?.user_connected) && (
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle>Available Courses</CardTitle>
                      <Button variant="outline" size="sm" onClick={fetchGoogleClassroomCourses}>
                        <RefreshCw className="w-4 h-4 mr-1" />
                        Refresh
                      </Button>
                    </div>
                    <CardDescription>
                      Select courses to sync to Zazu. Students will be automatically imported.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {gcLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                      </div>
                    ) : gcCourses.length === 0 ? (
                      <div className="text-center py-12">
                        <School className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                        <p className="text-slate-500">No courses found in Google Classroom</p>
                        <p className="text-sm text-slate-400 mt-1">Make sure you have active courses in your Google Classroom account</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {gcCourses.map((course) => (
                          <div 
                            key={course.id}
                            className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                          >
                            <div className="flex items-center gap-4">
                              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                                <BookOpen className="w-6 h-6 text-blue-600" />
                              </div>
                              <div>
                                <h3 className="font-semibold text-slate-800">{course.name}</h3>
                                <p className="text-sm text-slate-500">{course.section}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-xs bg-slate-200 px-2 py-0.5 rounded-full text-slate-600 flex items-center gap-1">
                                    <Users className="w-3 h-3" />
                                    {course.student_count} students
                                  </span>
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                                    course.state === 'ACTIVE' 
                                      ? 'bg-green-100 text-green-700' 
                                      : 'bg-slate-200 text-slate-600'
                                  }`}>
                                    {course.state}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <Button
                              onClick={() => handleGoogleClassroomSync(course.id)}
                              disabled={gcSyncing[course.id]}
                              className="bg-emerald-600 hover:bg-emerald-700"
                              data-testid={`gc-sync-${course.id}`}
                            >
                              {gcSyncing[course.id] ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                              ) : (
                                <Download className="w-4 h-4 mr-2" />
                              )}
                              Sync to Zazu
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Not Connected State */}
              {!gcStatus?.org_connected && !gcStatus?.user_connected && !gcLoading && (
                <Card>
                  <CardContent className="p-12 text-center">
                    <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <School className="w-10 h-10 text-blue-600" />
                    </div>
                    <h3 className="font-bold text-xl text-slate-800 mb-2">
                      Connect Google Classroom
                    </h3>
                    <p className="text-slate-600 mb-6 max-w-md mx-auto">
                      Import your organization's classes and students from Google Classroom. 
                      Teachers can then easily assign Zazu activities and track student progress.
                    </p>
                    <ul className="text-left max-w-sm mx-auto mb-6 space-y-2">
                      <li className="flex items-center gap-2 text-slate-600">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        Import classes automatically
                      </li>
                      <li className="flex items-center gap-2 text-slate-600">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        Sync student rosters
                      </li>
                      <li className="flex items-center gap-2 text-slate-600">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        Available to all teachers in your organization
                      </li>
                    </ul>
                    <Button 
                      onClick={handleGoogleClassroomConnect} 
                      disabled={gcConnecting}
                      size="lg"
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="gc-connect-main-btn"
                    >
                      {gcConnecting ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Link2 className="w-4 h-4 mr-2" />
                      )}
                      Connect Now
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Reset Password Dialog */}
      <Dialog open={resetDialog.open} onOpenChange={(open) => setResetDialog({ ...resetDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-500 mb-4">
              Set a new password for <strong>{resetDialog.userName}</strong>
            </p>
            <Label>New Password</Label>
            <Input
              type="password"
              placeholder="Enter new password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="mt-2"
              data-testid="new-password-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetDialog({ open: false, userId: null, userName: '' })}>
              Cancel
            </Button>
            <Button onClick={handleResetPassword} disabled={resetting}>
              {resetting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-500">
              Are you sure you want to delete <strong>{deleteDialog.userName}</strong>? This action cannot be undone.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, userId: null, userName: '' })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteUser} disabled={deleting}>
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Staff Dialog */}
      <Dialog open={addStaffDialog} onOpenChange={setAddStaffDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Staff Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Full Name *</Label>
              <Input
                placeholder="John Smith"
                value={newStaff.name}
                onChange={(e) => setNewStaff({ ...newStaff, name: e.target.value })}
                className="mt-1"
                data-testid="staff-name-input"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                placeholder="john@school.edu"
                value={newStaff.email}
                onChange={(e) => setNewStaff({ ...newStaff, email: e.target.value })}
                className="mt-1"
                data-testid="staff-email-input"
              />
            </div>
            <div>
              <Label>Password *</Label>
              <Input
                type="password"
                placeholder="Min. 6 characters"
                value={newStaff.password}
                onChange={(e) => setNewStaff({ ...newStaff, password: e.target.value })}
                className="mt-1"
                data-testid="staff-password-input"
              />
            </div>
            <div>
              <Label>Role</Label>
              <Select
                value={newStaff.role}
                onValueChange={(v) => setNewStaff({ ...newStaff, role: v })}
              >
                <SelectTrigger className="mt-1" data-testid="staff-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="teacher">Teacher</SelectItem>
                  <SelectItem value="org_admin">Organization Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddStaffDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddStaff} disabled={addingStaff} data-testid="submit-add-staff">
              {addingStaff && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Add Staff
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Class Dialog */}
      <Dialog open={addClassDialog} onOpenChange={setAddClassDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Class</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Class Name *</Label>
              <Input
                placeholder="e.g., Morning Stars, Grade 2 Math"
                value={newClass.name}
                onChange={(e) => setNewClass({ ...newClass, name: e.target.value })}
                className="mt-1"
                data-testid="class-name-input"
              />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input
                placeholder="Brief description of the class"
                value={newClass.description}
                onChange={(e) => setNewClass({ ...newClass, description: e.target.value })}
                className="mt-1"
                data-testid="class-desc-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Grade Level</Label>
                <Select
                  value={newClass.grade_level}
                  onValueChange={(v) => setNewClass({ ...newClass, grade_level: v })}
                >
                  <SelectTrigger className="mt-1" data-testid="class-grade-select">
                    <SelectValue placeholder="Select grade" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Pre-K">Pre-K</SelectItem>
                    <SelectItem value="Kindergarten">Kindergarten</SelectItem>
                    <SelectItem value="1st Grade">1st Grade</SelectItem>
                    <SelectItem value="2nd Grade">2nd Grade</SelectItem>
                    <SelectItem value="3rd Grade">3rd Grade</SelectItem>
                    <SelectItem value="4th Grade">4th Grade</SelectItem>
                    <SelectItem value="5th Grade">5th Grade</SelectItem>
                    <SelectItem value="6th Grade">6th Grade</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Max Students</Label>
                <Input
                  type="number"
                  value={newClass.max_students}
                  onChange={(e) => setNewClass({ ...newClass, max_students: parseInt(e.target.value) || 30 })}
                  className="mt-1"
                  data-testid="class-max-input"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddClassDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddClass} disabled={addingClass} data-testid="submit-create-class">
              {addingClass && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Create Class
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrgAdminDashboard;
